import { env } from 'lib/constants/env';
import type { CartLineItem } from 'types/cart/CartLineItem';
import type { ShopifyProduct } from 'types/product/ShopifyProduct';
import type { SubscriptionProduct } from 'types/product/SubscriptionProduct';
import type {
  SubscriptionUpsellOption,
  SubscriptionUpsellOptionWithServings,
} from 'types/product/SubscriptionUpsellOption';

import { isOneTimePurchase } from './typeInference';

const DEFAULT_UPSELL_CHOICES = 0;

export const createSubscriptionUpsellOptions = (
  lineItem: CartLineItem,
  shopifyProduct?: ShopifyProduct | null,
): (SubscriptionUpsellOption | SubscriptionUpsellOptionWithServings)[] => {
  const { NEXT_PUBLIC_UPSELL_CHOICES = DEFAULT_UPSELL_CHOICES } = env;

  /*
   * We only want to upsell if the upsell choices is more than 0
   * and if the product:
   * - has subscriptions
   * - has no discounts
   * - has no pre-upsell line items (ie, the customer has already been upsold)
   * - is NOT a single variant subscription product (we show sub's in the dropdown)
   */
  if (
    NEXT_PUBLIC_UPSELL_CHOICES === 0 ||
    !shopifyProduct ||
    !('subscriptions' in shopifyProduct) ||
    !!lineItem.discountAllocations.length ||
    !!lineItem.preUpsellLineItem ||
    (!lineItem.merchandise.servings && !isOneTimePurchase(lineItem))
  ) {
    return [];
  }

  const lineItemMerchandiseId = lineItem.merchandise.id;
  const { subscriptions } = shopifyProduct as SubscriptionProduct;

  // Filter out subscriptions not for sale or without servings
  const options = subscriptions.filter(
    (s): s is SubscriptionUpsellOptionWithServings | SubscriptionUpsellOption =>
      s.availableForSale,
  );

  // If it's a one time purchase AND multi-variant (Softgels),
  // upsell the equivalent subscription option
  if (
    isOneTimePurchase(lineItem) &&
    shopifyProduct.variants.length === options.length
  ) {
    return options.filter((opt) => opt.id === lineItemMerchandiseId);
  }
  // Otherwise upsell options that are more expensive than the current line item
  const startIndex = options.findIndex(
    (subscription) => subscription.id === lineItemMerchandiseId,
  );

  // If there is only one variant (such as The Night Cream) we just
  // want the first available subscription. */
  const isMultiVariant = shopifyProduct.variants.length > 1;

  // Limit the number of upsells we show
  const firstUpsell = startIndex + (isMultiVariant ? 1 : 0);
  const lastUpsell =
    firstUpsell + (isMultiVariant ? NEXT_PUBLIC_UPSELL_CHOICES : 1);

  return startIndex !== -1 ? options.slice(firstUpsell, lastUpsell) : [];
};
